/**
 * Modal: build line items from the menu (or custom lines) and create a quote order + customer pay link.
 * Uses portal login session (adminFetch) — same auth as Send reply on Contact messages.
 */

import { useState, useEffect } from 'react';
import { HiX, HiPlus, HiTrash } from 'react-icons/hi';
import { adminFetch, adminHeaders } from '../lib/adminApi';

export interface MenuItemRow {
  id: string;
  name: string;
  price: number;
  category: string;
}

type LineRow =
  | { key: string; kind: 'menu'; menu_item_id: string; quantity: number }
  | { key: string; kind: 'custom'; name: string; price: string; quantity: number };

interface Props {
  apiUrl: string;
  contactSubmissionId: string;
  contactName: string;
  onClose: () => void;
  onCreated: (payUrl: string, orderNumber: string) => void;
}

function newKey() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export default function CreateQuotePayLinkModal({
  apiUrl,
  contactSubmissionId,
  contactName,
  onClose,
  onCreated,
}: Props) {
  const [menuItems, setMenuItems] = useState<MenuItemRow[]>([]);
  const [menuLoading, setMenuLoading] = useState(true);
  const [lines, setLines] = useState<LineRow[]>([]);
  const [quoteRecap, setQuoteRecap] = useState('');
  const [pickupDate, setPickupDate] = useState('');
  const [pickupTime, setPickupTime] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const base = apiUrl || '';
    const url = base ? `${base}/api/menu` : '/api/menu';
    adminFetch(url)
      .then((r) => (r.ok ? r.json() : {}))
      .then((data: { items?: MenuItemRow[] }) => {
        setMenuItems(Array.isArray(data?.items) ? data.items : []);
      })
      .catch(() => setMenuItems([]))
      .finally(() => setMenuLoading(false));
  }, [apiUrl]);

  const addMenuLine = () => {
    const first = menuItems[0];
    setLines((prev) => [
      ...prev,
      {
        key: newKey(),
        kind: 'menu',
        menu_item_id: first?.id || '',
        quantity: 1,
      },
    ]);
  };

  const addCustomLine = () => {
    setLines((prev) => [
      ...prev,
      { key: newKey(), kind: 'custom', name: '', price: '', quantity: 1 },
    ]);
  };

  const submit = async () => {
    setError(null);
    if (lines.length === 0) {
      setError('Add at least one line item.');
      return;
    }

    const items: Array<
      { menu_item_id: string; quantity: number } | { name: string; price: number; quantity: number; category?: string }
    > = [];
    for (const row of lines) {
      if (row.kind === 'menu') {
        if (!row.menu_item_id) {
          setError('Select a menu item for each menu line.');
          return;
        }
        items.push({ menu_item_id: row.menu_item_id, quantity: row.quantity });
      } else {
        const price = parseFloat(row.price);
        if (!row.name.trim() || !Number.isFinite(price) || price < 0) {
          setError('Custom lines need a name and valid price.');
          return;
        }
        items.push({
          name: row.name.trim(),
          price,
          quantity: Math.max(1, row.quantity),
          category: 'Other',
        });
      }
    }

    setSubmitting(true);
    try {
      const base = apiUrl || '';
      const url = base ? `${base}/api/orders/from-contact-quote` : '/api/orders/from-contact-quote';
      const body: Record<string, unknown> = {
        contact_submission_id: contactSubmissionId,
        items,
        quote_recap: quoteRecap.trim() || undefined,
        send_pay_email: true,
      };
      if (pickupDate.trim()) {
        body.pickup_date = pickupDate.trim();
        if (pickupTime.trim()) body.pickup_time = pickupTime.trim();
      }

      const res = await adminFetch(url, {
        method: 'POST',
        headers: adminHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error((data as { error?: string; detail?: string }).error || (data as { detail?: string }).detail || 'Request failed');
      }
      const payUrl = (data as { pay_url?: string }).pay_url;
      const order = (data as { order?: { order_number?: string } }).order;
      if (!payUrl) throw new Error('No pay URL returned');
      onCreated(payUrl, order?.order_number ? String(order.order_number) : '');
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create quote');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
          <h2 className="text-lg font-semibold text-gray-900">Create pay link</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-gray-100 text-gray-500"
            aria-label="Close"
          >
            <HiX className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 space-y-4">
          <p className="text-sm text-gray-600">
            Quote for <span className="font-medium text-gray-900">{contactName}</span>. Customer receives an email with a secure pay link.
          </p>
          {error && <div className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-lg p-3">{error}</div>}

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={addMenuLine}
              disabled={menuLoading || menuItems.length === 0}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium bg-purple-100 text-purple-800 rounded-lg hover:bg-purple-200 disabled:opacity-50"
            >
              <HiPlus className="w-4 h-4" />
              Add from menu
            </button>
            <button
              type="button"
              onClick={addCustomLine}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium bg-gray-100 text-gray-800 rounded-lg hover:bg-gray-200"
            >
              <HiPlus className="w-4 h-4" />
              Custom line
            </button>
          </div>
          {menuLoading && <p className="text-sm text-gray-500">Loading menu…</p>}
          {!menuLoading && menuItems.length === 0 && (
            <p className="text-sm text-amber-800">No menu items loaded. Check API URL and try again.</p>
          )}

          <ul className="space-y-3">
            {lines.map((row, idx) => (
              <li key={row.key} className="flex flex-col gap-2 p-3 border border-gray-200 rounded-lg bg-gray-50/50">
                <div className="flex justify-between items-center">
                  <span className="text-xs font-medium text-gray-500">Line {idx + 1}</span>
                  <button
                    type="button"
                    onClick={() => setLines((prev) => prev.filter((l) => l.key !== row.key))}
                    className="text-red-600 hover:bg-red-50 p-1 rounded"
                    aria-label="Remove line"
                  >
                    <HiTrash className="w-4 h-4" />
                  </button>
                </div>
                {row.kind === 'menu' ? (
                  <div className="flex flex-col sm:flex-row gap-2">
                    <select
                      aria-label={`Menu item for line ${idx + 1}`}
                      value={row.menu_item_id}
                      onChange={(e) =>
                        setLines((prev) =>
                          prev.map((l) =>
                            l.key === row.key && l.kind === 'menu' ? { ...l, menu_item_id: e.target.value } : l
                          )
                        )
                      }
                      className="flex-1 text-sm border border-gray-300 rounded-lg px-2 py-1.5"
                    >
                      <option value="">Select item…</option>
                      {menuItems.map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.name} — ${Number(m.price).toFixed(2)} ({m.category})
                        </option>
                      ))}
                    </select>
                    <input
                      type="number"
                      min={1}
                      value={row.quantity}
                      onChange={(e) =>
                        setLines((prev) =>
                          prev.map((l) =>
                            l.key === row.key && l.kind === 'menu'
                              ? { ...l, quantity: Math.max(1, parseInt(e.target.value, 10) || 1) }
                              : l
                          )
                        )
                      }
                      className="w-24 text-sm border border-gray-300 rounded-lg px-2 py-1.5"
                      aria-label={`Quantity for line ${idx + 1}`}
                    />
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <input
                      placeholder="Name"
                      value={row.name}
                      onChange={(e) =>
                        setLines((prev) =>
                          prev.map((l) => (l.key === row.key && l.kind === 'custom' ? { ...l, name: e.target.value } : l))
                        )
                      }
                      className="text-sm border border-gray-300 rounded-lg px-2 py-1.5"
                    />
                    <input
                      placeholder="Price"
                      type="number"
                      step="0.01"
                      min="0"
                      value={row.price}
                      onChange={(e) =>
                        setLines((prev) =>
                          prev.map((l) => (l.key === row.key && l.kind === 'custom' ? { ...l, price: e.target.value } : l))
                        )
                      }
                      className="text-sm border border-gray-300 rounded-lg px-2 py-1.5"
                    />
                    <input
                      type="number"
                      min={1}
                      value={row.quantity}
                      onChange={(e) =>
                        setLines((prev) =>
                          prev.map((l) =>
                            l.key === row.key && l.kind === 'custom'
                              ? { ...l, quantity: Math.max(1, parseInt(e.target.value, 10) || 1) }
                              : l
                          )
                        )
                      }
                      className="text-sm border border-gray-300 rounded-lg px-2 py-1.5"
                      aria-label={`Quantity for custom line ${idx + 1}`}
                    />
                  </div>
                )}
              </li>
            ))}
          </ul>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Quote recap (optional)</label>
            <textarea
              value={quoteRecap}
              onChange={(e) => setQuoteRecap(e.target.value)}
              rows={3}
              className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2"
              placeholder="Short summary for the order notes…"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <label htmlFor="quote-pickup-date" className="block text-sm font-medium text-gray-700 mb-1">
                Pickup date (optional)
              </label>
              <input
                id="quote-pickup-date"
                type="date"
                value={pickupDate}
                onChange={(e) => setPickupDate(e.target.value)}
                className="w-full text-sm border border-gray-300 rounded-lg px-2 py-1.5"
              />
            </div>
            <div>
              <label htmlFor="quote-pickup-time" className="block text-sm font-medium text-gray-700 mb-1">
                Pickup time (optional)
              </label>
              <input
                id="quote-pickup-time"
                value={pickupTime}
                onChange={(e) => setPickupTime(e.target.value)}
                placeholder="e.g. 2:00 PM"
                className="w-full text-sm border border-gray-300 rounded-lg px-2 py-1.5"
              />
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 border-t border-gray-200 px-4 py-3">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg">
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={submitting}
            className="px-4 py-2 text-sm font-medium bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
          >
            {submitting ? 'Creating…' : 'Create & email pay link'}
          </button>
        </div>
      </div>
    </div>
  );
}
